import { z } from "zod";
import { System } from "./system";

export enum IsoCountryCode {
	None,
	SE,
	NO,
	DK,
	BE,
	EE,
	FI,
	US,
	RU,
	NL,
	ES,
	LT,
	LV,
	FR,
}

export enum UsernameType {
	Unknown = 0,
	SE = 1,
	DK = 2,
	NO = 3,
	Generic = 4,
	Email = 5,
	Otp = 6,
	Subject = 7,
	BE = 8,
	EE = 9,
	FI = 10,
	NL = 11,
	LT = 12,
	LV = 13,
}

export const LocaleIds = {
	DK: "da-DK",
	SE: "sv-SE",
	EN: "en-GB",
	NOb: "nb-NO",
	NO: "nb-NO",
	NOn: "nb-NO",
	FR: "fr-FR",
} as const;

export const LocaleIdSchema = z
	.object({
		locale: z.string(),
	})
	.refine((schema) => Array.from(Object.values(LocaleIds)).includes(schema.locale as LocaleId), {
		path: ["locale"],
		message: "Not a valid locale id",
	});

export type LocaleId = (typeof LocaleIds)[keyof typeof LocaleIds];

export interface IUser {
	systemBankAccounts: ISystemBankAccount[];
	systemPinCodes: ISystemPinCode[];
	name: string;
	firstname: string;
	lastname: string;
	username: string;
	mobilePhone: string;
	email: string;
	locale: LocaleId;
	loginType: number;
	coAddress: string;
	address: string;
	zip: string;
	city: string;
	country: string;
	countryCode: string;
	phone: string;
	contacts: IContact[];
	image: string;
	imageUrl: string;
	imageFilename: string;
	imageUploadData: string;
	disableImpersonate: boolean;
	usernameType: UsernameType;
	hasValidEmployment: boolean;
	employeeId: number;
	isManuallyConnectedToSystem: boolean;
	uniqueKey: string;
	subject: string;
	type: number;
	lastSeen: string;
	enabled: boolean;
}

const MAX_CONTACT_FIELD_LENGTH = 50;

export const ContactSchema = z.object({
	id: z.number().optional(),
	subject: z.string().optional(),
	name: z
		.string()
		.min(2, { message: "validation_name_at_least_2_characters" })
		.max(MAX_CONTACT_FIELD_LENGTH, { message: "validation_name_max_50_characters" }),
	// Required/format validation is owned by the PhoneInput component; length here is just an insurance check.
	phone: z.string().max(MAX_CONTACT_FIELD_LENGTH, { message: "validation_phone_max_50_characters" }),
	relation: z
		.string()
		.max(MAX_CONTACT_FIELD_LENGTH, { message: "validation_relation_max_50_characters" })
		.optional(),
});

export const ContactsSchema = z.object({
	contacts: z.array(ContactSchema),
});

export type IContact = z.infer<typeof ContactSchema>;

export const createContactRecord = (props: Partial<IContact>): IContact => {
	return {
		id: props.id ?? 0,
		subject: props.subject ?? "",
		name: props.name ?? "",
		phone: props.phone ?? "",
		relation: props.relation ?? "",
	};
};

export const LoginInfoSchema = z.object({
	username: z.string(),
	email: z
		.string()
		.min(1, { message: "validation_email_at_least_1_character" })
		.email("validation_email_must_be_valid"),
	mobilePhone: z.string().min(4, { message: "validation_phone_at_least_4_characters" }).or(z.literal("")),
});

export type LoginInfo = z.infer<typeof LoginInfoSchema>;

export const personalInfoSchema = z.object({
	firstname: z.string(),
	lastname: z.string(),
	coAddress: z.string(),
	address: z.string(),
	zip: z.string(),
	city: z.string(),
	country: z.string().optional(),
	countryCode: z.string().optional(),
	imageUrl: z.string().optional(),
	imageUploadData: z.string().optional(),
	imageFilename: z.string().optional(),
	image: z.string().optional(),
});
export type PersonalInfo = z.infer<typeof personalInfoSchema>;

interface ISystemPinCode {
	systemName: string;
	pinCode: string;
	systemCommonName: string;
}

export interface ISystemBankAccount {
	disabled: boolean;
	allowedIbanCountries?: string;
	bank?: string;
	systemName: string;
	accountNumber: string;
	ibanAccountNumber: string;
	ibanDisabled: boolean;
	systemCommonName: string;
	clearingNumber: string;
	isoCountryCode: IsoCountryCode;
	employeeId: string;
}

const SystemBankAccountSchema = z.object({
	disabled: z.boolean(),
	systemName: z.string(),
	allowedIbanCountries: z.string().optional().nullable(),
	bank: z.string().optional().nullable(),
	accountNumber: z.string(),
	ibanAccountNumber: z.string(),
	ibanDisabled: z.boolean(),
	systemCommonName: z.string(),
	clearingNumber: z.string(),
	isoCountryCode: z.enum(IsoCountryCode),
	paymentInfoId: z.number().optional().nullable(),
	employeeId: z.string(),
});

export const SystemBankAccountsSchema = z.object({ systemBankAccounts: z.array(SystemBankAccountSchema) });

export const createSystemBankAccountRecord = (
	system?: System,
	employeeIdForSystem?: number,
): ISystemBankAccount => {
	return {
		systemName: system?.name ?? "",
		systemCommonName: system?.commonName ?? "",
		isoCountryCode: system?.countryCode ?? IsoCountryCode.None,
		employeeId: employeeIdForSystem?.toString() ?? "-1",
		accountNumber: "",
		clearingNumber: "",
		ibanAccountNumber: "",
		ibanDisabled: false,
		disabled: false,
	};
};

export class User {
	readonly systemBankAccounts: ISystemBankAccount[];
	readonly systemPinCodes: ISystemPinCode[];
	readonly name: string;
	readonly firstname: string;
	readonly lastname: string;
	readonly username: string;
	readonly mobilePhone: string;
	readonly email: string;
	readonly locale: LocaleId;
	readonly loginType: number;
	readonly coAddress: string;
	readonly address: string;
	readonly zip: string;
	readonly city: string;
	readonly country: string;
	readonly countryCode: string;
	readonly phone: string;
	readonly contacts: IContact[];
	readonly image: string;
	readonly imageUrl: string;
	readonly imageFilename: string;
	readonly imageUploadData: string;
	readonly disableImpersonate: boolean;
	readonly usernameType: number;
	readonly hasValidEmployment: boolean;
	readonly employeeId: number;
	readonly isManuallyConnectedToSystem: boolean;
	readonly uniqueKey: string;
	readonly subject: string;
	readonly type: number;
	readonly lastSeen: string;
	readonly enabled: boolean;
	constructor(values: IUser) {
		this.systemBankAccounts = (values.systemBankAccounts ?? []).map((account) => ({
			...account,
			clearingNumber: account.clearingNumber ?? "",
			accountNumber: account.accountNumber ?? "",
			ibanAccountNumber: account.ibanAccountNumber ?? "",
		}));
		this.systemPinCodes = values.systemPinCodes ?? [];
		this.name = values.name ?? "";
		this.firstname = values.firstname ?? "";
		this.lastname = values.lastname ?? "";
		this.username = values.username ?? "";
		this.mobilePhone = values.mobilePhone ?? "";
		this.email = values.email ?? "";
		this.locale = values.locale ?? "";
		this.loginType = values.loginType ?? "";
		this.coAddress = values.coAddress ?? "";
		this.address = values.address ?? "";
		this.zip = values.zip ?? "";
		this.city = values.city ?? "";
		this.country = values.country ?? "";
		this.countryCode = values.countryCode ?? "";
		this.phone = values.phone ?? "";
		this.contacts = values.contacts ?? [];
		this.image = values.image ?? "";
		this.imageUrl = values.imageUrl ?? "";
		this.imageFilename = values.imageFilename ?? "";
		this.imageUploadData = values.imageUploadData ?? false;
		this.disableImpersonate = values.disableImpersonate ?? false;
		this.usernameType = values.usernameType ?? "";
		this.hasValidEmployment = values.hasValidEmployment ?? false;
		this.employeeId = values.employeeId ?? -1;
		this.isManuallyConnectedToSystem = values.isManuallyConnectedToSystem ?? false;
		this.uniqueKey = values.uniqueKey ?? "";
		this.subject = values.subject ?? "";
		this.type = values.type ?? -1;
		this.lastSeen = values.lastSeen ?? "";
		this.enabled = values.enabled ?? false;
	}
}
